import { DailySnapshot, HourlySnapshot } from '@/core/tokens';
import { isAddressWeth } from '@/domain/ticks/helpers';
import { Address, TICK_PRICE_BASE, addressEqual } from 'nftx.js';

function findMinMaxDays(arrays: { day: number }[][]) {
  let lowestDay = Number.MAX_VALUE;
  let highestDay = Number.MIN_VALUE;

  for (const array of arrays) {
    for (const item of array) {
      if (item.day < lowestDay) {
        lowestDay = item.day;
      }
      if (item.day > highestDay) {
        highestDay = item.day;
      }
    }
  }

  return { lowestDay, highestDay };
}

function normalizeAndPadData(
  originalData: {
    price: number;
    activeLiquidity: string;
    day: number;
    timestamp: number;
    vaultId: string;
  }[],
  lowestDay: number,
  highestDay: number
) {
  const paddedData = [];
  let lastValidEntry = null;

  // Create a map to store data points by 'day' for quick access
  const dataMap = new Map();
  for (const point of originalData) {
    dataMap.set(point.day, point);
  }

  for (let day = lowestDay; day <= highestDay; day++) {
    const dataPoint = dataMap.get(day);

    if (dataPoint) {
      // Data point exists for this day, add it to the result
      paddedData.push(dataPoint);
      lastValidEntry = dataPoint;
    } else {
      // Data point doesn't exist, pad with the last valid entry
      if (lastValidEntry) {
        const paddedPoint = {
          day,
          timestamp: lastValidEntry.timestamp,
          price: lastValidEntry.price,
          activeLiquidity: lastValidEntry.activeLiquidity,
          vaultId: lastValidEntry.vaultId,
        };
        paddedData.push(paddedPoint);
      } else {
        // If there's no valid entry, pad it out with null values and the correct 'day'
        // so arrays are same length
        const paddedPoint = {
          day,
          timestamp: null,
          price: null,
          activeLiquidity: null,
          vaultId: null,
        };
        paddedData.push(paddedPoint);
      }
    }
  }

  return paddedData;
}

const formatData = (
  dailySnapshots: DailySnapshot[],
  network: number,
  vaultId: string,
  tokenAddress: Address,
  startDay: number,
  endDay: number
) => {
  const data = Object.values(
    dailySnapshots.reduce(
      (acc, snapshot: DailySnapshot) => {
        const isToken0 = addressEqual(snapshot.token0, tokenAddress);
        const isWeth0 = isAddressWeth(snapshot.token0, network);

        // if neither token is weth, abort
        if (!isToken0 && !isWeth0) {
          return acc;
        }

        let price = TICK_PRICE_BASE ** Number(snapshot.tick);
        if (isWeth0) {
          price = 1 / price;
        }
        const day = snapshot.day;
        if (!acc[day]) {
          acc[day] = {
            day,
            timestamp: snapshot.timestamp,
            price,
            activeLiquidity: snapshot.activeLiquidity.toString(),
            vaultId: vaultId,
          };
        }
        return acc;
      },
      {} as Record<
        number,
        {
          price: number;
          activeLiquidity: string;
          day: number;
          timestamp: number;
          vaultId: string;
        }
      >
    )
  ).slice(-168);

  return normalizeAndPadData(data, startDay, endDay);
};

export const buildDataForChart = (
  snapshots: {
    vaultId: string;
    tokenAddress: Address;
    hourlySnapshots: HourlySnapshot[];
    dailySnapshots: DailySnapshot[];
  }[],
  network: number
) => {
  // const today
  const today = Math.floor(Date.now() / 1000 / 60 / 60 / 24);
  const { lowestDay } = findMinMaxDays(snapshots.map((x) => x.dailySnapshots));

  return snapshots.map((x) =>
    formatData(
      x.dailySnapshots,
      network,
      x.vaultId,
      x.tokenAddress,
      lowestDay,
      // ensure chart always fills to current day
      today
    ).sort((a, b) => b.day - a.day)
  );
};
